import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import * as React from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../config/axios';
import env from '../../../config/env';
import EmployeeService from '../services/EmployeeService';
import { Employee } from '../../../hooks/useEmployeeData';
import { FaSort, FaSortUp, FaSortDown, FaPlus, FaUsers, FaMoneyCheckAlt, FaUserPlus, FaFileAlt, FaUpload } from 'react-icons/fa';
import ImportEmployeesModal from './ImportEmployeesModal';
import ComingSoonBadge from '../../../components/ComingSoonBadge';
import EmployeeAvatar from '../../../components/EmployeeAvatar';
import { personalakteStyles as ps, colors, shadows } from '../styles/personalakteStyles';
import { useUserRole } from '../../../hooks/useUserRole';
import Pagination from '../../../components/Pagination';
import ActiveFiltersBar, { ActiveFilter } from '../../../components/ActiveFiltersBar';
import dialogUtil from '../../../shared/utils/dialog';
import { TableSkeleton, employeeColumns } from '../../../components/Skeleton';
import EmployeeActionMenu from './EmployeeActionMenu';
import EmployeeInfoBanner from './EmployeeInfoBanner';
import AddEmployeeModal from './AddEmployeeModal';
import ChangePasswordModal from './ChangePasswordModal';
import FinalizeEmployeeModal from './FinalizeEmployeeModal';

type EmployeesTabKey = 'employees' | 'payrolls' | 'recruiting' | 'documents';

const TABS: { key: EmployeesTabKey; label: string; icon: React.ReactNode; disabled?: boolean }[] = [
  { key: 'employees', label: 'Employees', icon: <FaUsers /> },
  { key: 'payrolls', label: 'Payrolls', icon: <FaMoneyCheckAlt /> },
  { key: 'documents', label: 'Documents', icon: <FaFileAlt /> },
  { key: 'recruiting', label: 'Recruiting', icon: <FaUserPlus /> },
];

const ADMIN_ONLY_TABS: EmployeesTabKey[] = ['payrolls', 'documents'];

function Employees() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useUserRole();
  const visibleTabs = isAdmin ? TABS : TABS.filter(t => !ADMIN_ONLY_TABS.includes(t.key));
  // ----------------------------------
  // 1) State: Liste, Ladezustand, etc.
  // ----------------------------------
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // WorkArea-Liste/Map, um IDs -> Namen zuzuordnen
  const [workAreaMap, setWorkAreaMap] = useState({});

  // ----------------------------------
  // 2) Tabs
  // ----------------------------------
  const [activeTab, setActiveTab] = useState('employees');

  // ----------------------------------
  // 3) Add-Modal
  // ----------------------------------
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [highlightedEmails, setHighlightedEmails] = useState<Set<string>>(new Set());

  // ----------------------------------
  // 4) Modals: Finalize, Password, EmployeeDetails
  // ----------------------------------
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedEmployeeForPassword, setSelectedEmployeeForPassword] = useState(null);


  // Loading / error states
  const [activatingUser, setActivatingUser] = useState(null);

  // (NEU) Resend Invite
  const [resendInviteEmployeeId, setResendInviteEmployeeId] = useState(null);
  const [resendError, setResendError] = useState('');

  // (NEU) Ein Actions-Dropdown pro Zeile
  const [openActionMenuFor, setOpenActionMenuFor] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState<{top: number, left: number} | null>(null);

  // State für Suche und Filter
  const [searchTerm, setSearchTerm] = useState("");
  // hiddenWorkAreas = WAs die ausgeblendet sind. Leer = alle sichtbar (default).
  const [hiddenWorkAreas, setHiddenWorkAreas] = useState<number[]>([]);
  const [onboardingStatusFilter, setOnboardingStatusFilter] = useState<string[]>([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showWADropdown, setShowWADropdown] = useState(false);
  const [showDeactivatedSection, setShowDeactivatedSection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const deactivatedRef = useRef<HTMLDivElement>(null);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const waFilterRef = useRef<HTMLDivElement>(null);

  // Bulk selection
  const [selectedEmpIds, setSelectedEmpIds] = useState<Set<number>>(new Set());
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [bulkPasswordOpen, setBulkPasswordOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkMenuRef = useRef<HTMLDivElement>(null);

  // Click-outside handler for status modal
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(e.target as Node)) {
        setShowStatusModal(false);
      }
    };
    if (showStatusModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStatusModal]);

  // Click-outside handler for WA dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (waFilterRef.current && !waFilterRef.current.contains(e.target as Node)) {
        setShowWADropdown(false);
      }
    };
    if (showWADropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWADropdown]);

  // Clear bulk selection and reset page on filter/tab change
  useEffect(() => {
    setSelectedEmpIds(new Set());
    setCurrentPage(1);
  }, [activeTab, JSON.stringify(onboardingStatusFilter), JSON.stringify(hiddenWorkAreas), searchTerm]);

  // Click-outside handler for bulk menu
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target as Node)) {
        setBulkMenuOpen(false);
      }
    };
    if (bulkMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [bulkMenuOpen]);

  // ----------------------------------
  // Sorting (muss VOR early returns stehen)
  // ----------------------------------
  type SortKey = 'name' | 'email' | 'workAreas' | 'weekly' | 'employmentType' | 'balance' | 'start' | 'status';
  const [sortBy, setSortBy] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const toggleSort = (key: SortKey): void => {
    if (sortBy === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const renderSortIcon = (key: SortKey): React.ReactNode => {
    const isActive = sortBy === key;
    const color = isActive ? colors.navy : colors.textMuted;
    const style = { marginLeft: 6, verticalAlign: 'middle' } as React.CSSProperties;
    if (!isActive) return <FaSort style={style} color={color} />;
    return sortDir === 'asc' ? <FaSortUp style={style} color={color} /> : <FaSortDown style={style} color={color} />;
  };

  // ----------------------------------
  // 5) useEffect => Employees, WorkAreas
  // ----------------------------------
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        let employees;
        if (onboardingStatusFilter.length > 0) {
          employees = await EmployeeService.fetchEmployeesByStatus(onboardingStatusFilter);
        } else {
          employees = await EmployeeService.fetchAllEmployees();
        }
        setEmployees(employees);
        setLoading(false);
      } catch (err) {
        console.error('Fehler beim Laden der Employees:', err);
        setError(err);
        setLoading(false);
      }
    };

    loadEmployees();

    axiosInstance.get(`/api/work-areas/`)
      .then((res) => {
        const map = {};
        res.data.filter((wa: any) => wa.is_active !== false).forEach((wa: any) => {
          map[wa.id] = { name: wa.name, color: wa.color || '#5271A5' };
        });
        setWorkAreaMap(map);
      })
      .catch((err) => {
        console.error('Fehler beim Laden der WorkAreas:', err);
      });
  }, [JSON.stringify(onboardingStatusFilter)]);

  // ----------------------------------
  // 6) CREATE: Neues Employee Invite
  // ----------------------------------
  const handleOpenAddModal = () => {
    setShowAddModal(true);
  };
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    if (searchParams.get('addEmployee')) {
      searchParams.delete('addEmployee');
      setSearchParams(searchParams);
    }
  };

  // Open modal via query param (from QuickAddButton)
  useEffect(() => {
    if (searchParams.get('addEmployee') === 'true') {
      handleOpenAddModal();
    }
  }, [searchParams]);

  const reloadEmployeeList = async () => {
    setLoading(true);
    try {
      let emps;
      if (onboardingStatusFilter.length > 0) {
        emps = await EmployeeService.fetchEmployeesByStatus(onboardingStatusFilter);
      } else {
        emps = await EmployeeService.fetchAllEmployees();
      }
      setEmployees(emps);
    } catch (err) {
      console.error('Fehler beim Neuladen:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteNewEmployee = (data: { first_name: string; last_name: string; email: string }) => {
    axiosInstance.post(`/api/admin/users/invitations/`, data)
      .then(response => {
        setShowAddModal(false);
        reloadEmployeeList();
      })
      .catch(err => {
        console.error('Fehler beim Erstellen:', err);
        toast.error(`Fehler beim Erstellen: ${err.message}`);
      });
  };

  // ----------------------------------
  // 7) Finalize Onboarding
  // ----------------------------------
  const handleOpenFinalizeModal = (emp) => {
    setSelectedEmployee(emp);
    setShowFinalizeModal(true);
    setOpenActionMenuFor(null);
  };
  const handleCloseFinalizeModal = () => {
    setShowFinalizeModal(false);
    setSelectedEmployee(null);
  };
  const handleFinalizeEmployee = () => {
    if (!selectedEmployee) return;
    axiosInstance.post(`/api/admin/employees/${selectedEmployee.id}/activate/`)
      .then((response) => {
        setEmployees(prev =>
          prev.map(emp => emp.id === selectedEmployee.id ? response.data : emp)
        );
        handleCloseFinalizeModal();
      })
      .catch((err) => {
        console.error('Error finalizing employee:', err);
      });
  };

  // ----------------------------------
  // 8) Password Change
  // ----------------------------------
  const handleOpenPasswordModal = (emp) => {
    setSelectedEmployeeForPassword(emp);
    setShowPasswordModal(true);
    setOpenActionMenuFor(null);
  };
  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedEmployeeForPassword(null);
  };

  const handleChangePassword = (password: string) => {
    axiosInstance.post(`/api/admin/employees/${selectedEmployeeForPassword.id}/override-password/`, {
      newPassword: password
    })
      .then(() => {
        handleClosePasswordModal();
        toast.success('Passwort erfolgreich geändert');
      })
      .catch(err => {
        console.error('Fehler beim Ändern des Passworts:', err);
        toast.error('Fehler beim Ändern des Passworts');
      });
  };

  // ----------------------------------
  // 9) EmployeeDetails - navigate to Personalakte
  // ----------------------------------
  const handleEmployeeUpdate = (updatedEmployee) => {
    setEmployees(prev =>
      prev.map(emp => emp.id === updatedEmployee.id ? updatedEmployee : emp)
    );
  };

  // ----------------------------------
  // 10) Activate-User
  // ----------------------------------
  const handleActivateUser = (employeeId) => {
    setActivatingUser(employeeId);
    setOpenActionMenuFor(null);
    axiosInstance.post(`/api/employees/${employeeId}/activate-user/`)
      .then(() => {
        // reload
        setLoading(true);
        const reloadEmployees = async () => {
          try {
            let employees;
            if (onboardingStatusFilter.length > 0) {
              employees = await EmployeeService.fetchEmployeesByStatus(onboardingStatusFilter);
            } else {
              employees = await EmployeeService.fetchAllEmployees();
            }
            setEmployees(employees);
            setLoading(false);
          } catch (err) {
            console.error('Error reloading employees:', err);
            setLoading(false);
          }
        };
        reloadEmployees();
      })
      .catch(err => {
        console.error('Error activating user:', err);
        toast.error('Error activating user');
      })
      .finally(() => {
        setActivatingUser(null);
      });
  };

  // ----------------------------------
  // 10a) Reactivate-User
  // ----------------------------------
  const handleReactivateUser = (employeeId) => {
    setActivatingUser(employeeId);
    setOpenActionMenuFor(null);
    axiosInstance.post(`/api/admin/employees/${employeeId}/reactivate/`)
      .then(() => {
        // reload
        setLoading(true);
        const reloadEmployees = async () => {
          try {
            let employees;
            if (onboardingStatusFilter.length > 0) {
              employees = await EmployeeService.fetchEmployeesByStatus(onboardingStatusFilter);
            } else {
              employees = await EmployeeService.fetchAllEmployees();
            }
            setEmployees(employees);
            setLoading(false);
          } catch (err) {
            console.error('Error reloading employees:', err);
            setLoading(false);
          }
        };
        reloadEmployees();
      })
      .catch(err => {
        console.error('Error reactivating user:', err);
        toast.error('Error reactivating user');
      })
      .finally(() => {
        setActivatingUser(null);
      });
  };

  // ----------------------------------
  // 10b) Deactivate-User
  // ----------------------------------
  const handleDeactivateUser = async (employeeId) => {
    const confirmed = await dialogUtil.confirm('Deactivate User', 'Are you sure you want to deactivate this user?', { variant: 'error', confirmLabel: 'Deactivate' });
    if (!confirmed) return;
    setActivatingUser(employeeId);
    setOpenActionMenuFor(null);
    axiosInstance.post(`/api/admin/employees/${employeeId}/deactivate/`)
      .then(() => {
        // reload
        setLoading(true);
        const reloadEmployees = async () => {
          try {
            let employees;
            if (onboardingStatusFilter.length > 0) {
              employees = await EmployeeService.fetchEmployeesByStatus(onboardingStatusFilter);
            } else {
              employees = await EmployeeService.fetchAllEmployees();
            }
            setEmployees(employees);
            setLoading(false);
          } catch (err) {
            console.error('Error reloading employees:', err);
            setLoading(false);
          }
        };
        reloadEmployees();
      })
      .catch(err => {
        console.error('Error deactivating user:', err);
        toast.error('Error deactivating user');
      })
      .finally(() => {
        setActivatingUser(null);
      });
  };

  // ----------------------------------
  // (NEU) Resend Invite
  // ----------------------------------
  const handleResendInvite = (emp) => {
    setResendError('');
    setResendInviteEmployeeId(emp.id);
    setOpenActionMenuFor(null);

    axiosInstance.post(`/api/admin/employees/${emp.id}/resend-invite/`)
      .then(() => {
        toast.success('Invite resent successfully');
      })
      .catch(err => {
        console.error('Error resending invite:', err);
        setResendError(`Error resending invite: ${err.message}`);
      })
      .finally(() => {
        setResendInviteEmployeeId(null);
      });
  };

  // ----------------------------------
  // Bulk Actions
  // ----------------------------------
  const handleBulkStatusChange = async (action: 'activate' | 'deactivate' | 'reactivate') => {
    const ids = Array.from(selectedEmpIds);
    if (action === 'deactivate') {
      const confirmed = await dialogUtil.confirm('Deactivate Employees', `Are you sure you want to deactivate ${ids.length} employee(s)?`, { variant: 'error', confirmLabel: 'Deactivate' });
      if (!confirmed) return;
    }
    setBulkBusy(true);
    const endpointMap: Record<string, (id: number) => string> = {
      activate: (id) => `/api/employees/${id}/activate-user/`,
      deactivate: (id) => `/api/admin/employees/${id}/deactivate/`,
      reactivate: (id) => `/api/employees/${id}/reactivate/`,
    };
    const results = await Promise.allSettled(
      ids.map(id => axiosInstance.post(endpointMap[action](id)))
    );
    const ok = results.filter(r => r.status === 'fulfilled').length;
    const fail = results.filter(r => r.status === 'rejected').length;
    if (ok > 0) toast.success(`${ok} employee(s) ${action}d`);
    if (fail > 0) toast.warning(`${fail} failed`);
    setSelectedEmpIds(new Set());
    setBulkMenuOpen(false);
    await reloadEmployeeList();
    setBulkBusy(false);
  };

  const handleBulkPasswordChange = async (password: string) => {
    const ids = Array.from(selectedEmpIds);
    setBulkBusy(true);
    const results = await Promise.allSettled(
      ids.map(id => axiosInstance.post(`/api/admin/employees/${id}/override-password/`, { newPassword: password }))
    );
    const ok = results.filter(r => r.status === 'fulfilled').length;
    const fail = results.filter(r => r.status === 'rejected').length;
    if (ok > 0) toast.success(`Password changed for ${ok} employee(s)`);
    if (fail > 0) toast.warning(`${fail} failed`);
    setBulkPasswordOpen(false);
    setSelectedEmpIds(new Set());
    setBulkBusy(false);
  };

  // ----------------------------------
  // 11) Buttons / Dropdown
  // ----------------------------------
  const toggleActionMenu = (employeeId, buttonEl?: HTMLElement) => {
    if (openActionMenuFor === employeeId) {
      setOpenActionMenuFor(null);
      setActionMenuPos(null);
    } else {
      setOpenActionMenuFor(employeeId);
      if (buttonEl) {
        const rect = buttonEl.getBoundingClientRect();
        const menuHeight = 220;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openAbove = spaceBelow < menuHeight && rect.top > menuHeight;
        setActionMenuPos({
          top: openAbove ? rect.top - menuHeight - 2 : rect.bottom + 2,
          left: rect.right
        });
      }
    }
  };

  // ----------------------------------
  // Loading / Error
  // ----------------------------------
  if (loading) {
    return (
      <div style={{ padding: '16px' }}>
        <TableSkeleton columns={employeeColumns} rows={10} />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding:'20px', color:'red' }}>
        <p>Fehler beim Laden der Employees!</p>
      </div>
    );
  }

  // ----------------------------------
  // Hilfsfunktionen
  // ----------------------------------
  const formatDateDDMMYYYY = (isoDate) => {
    if (!isoDate) return '—';
    try {
      const dt = new Date(isoDate);
      const dd = String(dt.getDate()).padStart(2,'0');
      const mm = String(dt.getMonth()+1).padStart(2,'0');
      const yyyy = dt.getFullYear();
      return `${dd}.${mm}.${yyyy}`;
    } catch(e) {
      return isoDate;
    }
  };

  const computeNameBirth = (emp) => {
    let birth = formatDateDDMMYYYY(emp.birth_date);
    if (birth === '—') birth = '';
    return `${emp.first_name} ${emp.last_name} ${birth? '('+birth+')':''}`.trim();
  };

  const computeWorkAreas = (emp) => {
    if (!emp.work_areas || !Array.isArray(emp.work_areas) || emp.work_areas.length === 0) {
      return '—';
    }
    const names = emp.work_areas.map(waId => {
      const wa = workAreaMap[waId];
      return wa?.name || `WA#${waId}`;
    });
    return names.join(', ');
  };

  const computeWeeklyHours = (emp) => {
    const rawHours = emp.min_hours || emp.contract_hours_per_week || emp.hours_per_week || emp.weekly_hours || 0;
    const hours = Number(rawHours) % 1 === 0 ? Math.round(Number(rawHours)) : Number(rawHours);
    if (emp.employment_type) {
      return `${emp.employment_type} (${hours} Std)`;
    }
    return hours ? `${hours} Std` : '—';
  };

  const computeTotalBalance = (emp: Employee) => '+12'; // Demo
  const computeQualif       = (emp: Employee) => 'Review Champ'; // Demo

  const computeDocs = (emp) => {
    const redCardBadge = emp.infektionsschutz_document ? 'greenBadge' : 'greyBadge';
    const immaBadge     = emp.imma_document ? 'greenBadge' : 'greyBadge';
    const contractBadge = emp.arbeitsvertrag_document ? 'greenBadge' : 'greyBadge';
    return (
      <div style={{ display:'flex', gap:'4px' }}>
        <span style={badgeStyle(redCardBadge)}>Rk</span>
        <span style={badgeStyle(immaBadge)}>Im</span>
        <span style={badgeStyle(contractBadge)}>Vt</span>
      </div>
    );
  };

  const renderAvatar = (emp: Employee) => {
    return (
      <EmployeeAvatar
        firstName={emp.first_name}
        lastName={emp.last_name}
        photoUrl={emp.photo_url}
        size={28}
      />
    );
  };

  // ----------------------------------
  // TABS + Render
  // ----------------------------------

  const filteredEmployees = employees.filter(emp => {
    const name = (emp.first_name + ' ' + emp.last_name).toLowerCase();
    const matchesName = name.includes(searchTerm.toLowerCase());
    // WA Filter: Employee muss mindestens eine sichtbare WA haben (nicht in hiddenWorkAreas)
    // Wenn hiddenWorkAreas leer ist, sind alle sichtbar
    const matchesWA = hiddenWorkAreas.length === 0 ||
      (emp.work_areas && emp.work_areas.some((waId: number) => !hiddenWorkAreas.includes(waId)));
    return matchesName && matchesWA;
  });
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const nameA = (a.last_name + ' ' + a.first_name).toLowerCase();
    const nameB = (b.last_name + ' ' + b.first_name).toLowerCase();
    const emailA = String(a.email || '').toLowerCase();
    const emailB = String(b.email || '').toLowerCase();
    const waA = computeWorkAreas(a).toLowerCase();
    const waB = computeWorkAreas(b).toLowerCase();
    const weeklyA = computeWeeklyHours(a).toLowerCase();
    const weeklyB = computeWeeklyHours(b).toLowerCase();
    const employmentTypeA = String(a.employment_type || '').toLowerCase();
    const employmentTypeB = String(b.employment_type || '').toLowerCase();
    const balanceA = (a as any).balance_h !== undefined ? (a as any).balance_h : 0;
    const balanceB = (b as any).balance_h !== undefined ? (b as any).balance_h : 0;
    const startA = String(a.start_date || '');
    const startB = String(b.start_date || '');
    const statusA = String(a.onboarding_status || '').toLowerCase();
    const statusB = String(b.onboarding_status || '').toLowerCase();

    switch (sortBy) {
      case 'email':
        return emailA.localeCompare(emailB) * dir;
      case 'workAreas':
        return waA.localeCompare(waB) * dir;
      case 'weekly':
        return weeklyA.localeCompare(weeklyB) * dir;
      case 'employmentType':
        return employmentTypeA.localeCompare(employmentTypeB) * dir;
      case 'balance':
        return (balanceA - balanceB) * dir;
      case 'start':
        return startA.localeCompare(startB) * dir;
      case 'status':
        return statusA.localeCompare(statusB) * dir;
      case 'name':
      default:
        return nameA.localeCompare(nameB) * dir;
    }
  });

  const activeEmployees = sortedEmployees.filter(e => e.onboarding_status !== 'DEACTIVATED');
  const paginatedActiveEmployees = activeEmployees.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const deactivatedEmployees = sortedEmployees.filter(e => e.onboarding_status === 'DEACTIVATED');

  const renderEmployeesTable = () => {
    const thStyle: React.CSSProperties = {
      padding: '8px 4px',
      textAlign: 'left',
      position: 'sticky',
      top: 0,
      background: colors.bgMuted,
      zIndex: 2,
      fontWeight: 600,
      fontSize: '11px',
      color: colors.textMuted,
      letterSpacing: '0.2px',
      border: 'none',
      whiteSpace: 'nowrap',
    };

    const tdStyle: React.CSSProperties = {
      padding: '4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };

    return (
      <div style={{
        background: colors.bgCard,
        borderRadius: '16px',
        border: `1px solid ${colors.border}`,
        boxShadow: shadows.card,
        minHeight: 'calc(100vh - 200px)',
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
        zIndex: 1,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' }}>
          {/* Photo, Name, Email, Work Areas, Weekly, Balance, Start, Ende, Status, Actions */}
          <colgroup><col style={{ width: '36px' }} /><col style={{ width: '160px' }} /><col style={{ width: '140px' }} /><col style={{ width: '120px' }} /><col style={{ width: '120px' }} /><col style={{ width: '70px' }} /><col style={{ width: '85px' }} /><col style={{ width: '85px' }} /><col style={{ width: '100px' }} /><col style={{ width: '50px' }} /></colgroup>
          <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
            <tr style={{ borderBottom: 'none' }}>
              <th style={{ ...thStyle, textAlign: 'center' }}>Photo</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('name')}>Name {renderSortIcon('name')}</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('email')}>Email {renderSortIcon('email')}</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('workAreas')}>Work Areas {renderSortIcon('workAreas')}</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('weekly')}>Weekly {renderSortIcon('weekly')}</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('balance')}>Balance {renderSortIcon('balance')}</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('start')}>Start {renderSortIcon('start')}</th>
              <th style={thStyle}>Ende</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status {renderSortIcon('status')}</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody data-testid="employee-table-body">
            {paginatedActiveEmployees.map((emp: Employee) => {
              const displayName  = computeNameBirth(emp);
              const waNames      = computeWorkAreas(emp);
              const weeklyHours  = computeWeeklyHours(emp);
              const startDate    = formatDateDDMMYYYY(emp.start_date);
              const detailHref = `/employees/${emp.id}${location.search || ''}`;

              const isHighlighted = highlightedEmails.has((emp.email || '').toLowerCase());
              const isSelected = isAdmin && selectedEmpIds.has(emp.id);

              const rowBg = isSelected
                ? 'rgba(231, 0, 125, 0.07)'
                : isHighlighted
                  ? 'rgba(231, 0, 125, 0.06)'
                  : 'transparent';

              return (
                <tr
                  key={emp.id}
                  data-testid={`employee-row-${emp.id}`}
                  style={{
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    background: rowBg,
                    ...(isSelected ? {
                      boxShadow: `inset 3px 0 0 0 ${colors.magenta}`,
                    } : isHighlighted ? {
                      boxShadow: `inset 0 0 0 1px rgba(231, 0, 125, 0.15), 0 0 12px rgba(231, 0, 125, 0.1)`,
                      animation: 'importGlow 2s ease-in-out',
                    } : {}),
                  }}
                  onClick={() => {
                    if (isAdmin) {
                      setSelectedEmpIds(prev => {
                        const next = new Set(prev);
                        if (next.has(emp.id)) next.delete(emp.id); else next.add(emp.id);
                        return next;
                      });
                    }
                  }}
                  onMouseEnter={e => { if (!isHighlighted && !isSelected) e.currentTarget.style.background = colors.bgHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}
                >
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {renderAvatar(emp)}
                  </td>
                  <td style={tdStyle}>
                    <Link
                      to={detailHref}
                      data-testid={`emp-link-${emp.id}`}
                      aria-label="Open employee detail"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        color: colors.slate,
                        textDecoration: 'none',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                    >
                      {displayName}
                    </Link>
                  </td>
                  <td style={tdStyle}>{emp.email}</td>
                  <td style={tdStyle}>{waNames}</td>
                  <td style={tdStyle}>{weeklyHours}</td>
                  <td style={tdStyle}>
                    {(emp as any).balance_h !== undefined ? (
                      <span style={{ fontWeight: 600, color: (emp as any).balance_h >= 0 ? '#16a34a' : '#dc2626' }}>
                        {(emp as any).balance_h >= 0 ? '+' : ''}{((emp as any).balance_h).toFixed(1)}h
                      </span>
                    ) : '-'}
                  </td>
                  <td style={tdStyle}>{startDate}</td>
                  <td style={tdStyle}>{formatDateDDMMYYYY(emp.scheduled_deactivation_date) || formatDateDDMMYYYY(emp.contract_end_date) || '-'}</td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: '10px', color: colors.textMuted }}>{emp.onboarding_status || '-'}</span>
                  </td>
                  <td style={{ ...tdStyle, position: 'relative' }} onClick={e => e.stopPropagation()}>
                    <button
                      style={{
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: colors.textMuted,
                        padding: 0,
                        width: 28,
                        height: 28,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(82, 113, 165, 0.1)';
                        e.currentTarget.style.color = colors.slate;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = colors.bgCard;
                        e.currentTarget.style.color = colors.textMuted;
                      }}
                      onClick={(e) => toggleActionMenu(emp.id, e.currentTarget)}
                      title="Actions"
                      data-testid={`employee-action-menu-${emp.id}`}
                    >
                      ⋮
                    </button>
                  </td>
                </tr>
              );
            })}
            {paginatedActiveEmployees.length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: '16px', color: colors.textMuted, textAlign: 'center' }}>No employees found.</td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          totalItems={activeEmployees.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />

        {deactivatedEmployees.length > 0 && (
          <>
            <div
              ref={deactivatedRef}
              onClick={() => {
                const willOpen = !showDeactivatedSection;
                setShowDeactivatedSection(willOpen);
                if (willOpen) {
                  setTimeout(() => {
                    deactivatedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 50);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                cursor: 'pointer',
                borderTop: `1px solid ${colors.border}`,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{
                fontSize: 10,
                color: colors.textLight,
                transition: 'transform 0.2s',
                transform: showDeactivatedSection ? 'rotate(90deg)' : 'rotate(0deg)',
              }}>
                ▶
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, color: colors.danger }}>
                Deactivated ({deactivatedEmployees.length})
              </span>
            </div>

            {showDeactivatedSection && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed', opacity: 0.85 }}>
                <colgroup><col style={{ width: '36px' }} /><col style={{ width: '160px' }} /><col style={{ width: '140px' }} /><col style={{ width: '120px' }} /><col style={{ width: '120px' }} /><col style={{ width: '70px' }} /><col style={{ width: '85px' }} /><col style={{ width: '85px' }} /><col style={{ width: '100px' }} /><col style={{ width: '50px' }} /></colgroup>
                <tbody>
                  {deactivatedEmployees.map((emp: Employee) => {
                    const displayName  = computeNameBirth(emp);
                    const waNames      = computeWorkAreas(emp);
                    const startDate    = formatDateDDMMYYYY(emp.start_date);
                    const detailHref = `/employees/${emp.id}${location.search || ''}`;

                    return (
                      <tr
                        key={emp.id}
                        style={{ borderBottom: '1px solid #eee', cursor: 'pointer', transition: 'background 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <td style={{ ...tdStyle, textAlign: 'center', filter: 'grayscale(100%)', opacity: 0.5 }}>
                          {renderAvatar(emp)}
                        </td>
                        <td style={tdStyle}>
                          <Link
                            to={detailHref}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              color: colors.textBody,
                              textDecoration: 'none',
                              fontWeight: 500,
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                          >
                            {displayName}
                          </Link>
                        </td>
                        <td style={tdStyle}>{emp.email}</td>
                        <td style={tdStyle}>{waNames}</td>
                        <td style={tdStyle}>-</td>
                        <td style={tdStyle}>-</td>
                        <td style={tdStyle}>{startDate}</td>
                        <td style={tdStyle}>{formatDateDDMMYYYY(emp.deactivated_at) || '-'}</td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: '10px', color: '#dc2626', fontWeight: 600, background: colors.dangerLight, padding: '2px 6px', borderRadius: 4 }}>DEACTIVATED</span>
                        </td>
                        <td style={{ ...tdStyle, position: 'relative' }} onClick={e => e.stopPropagation()}>
                          <button
                            style={{
                              background: colors.bgCard,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '10px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              color: colors.textMuted,
                              padding: 0,
                              width: 28,
                              height: 28,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = 'rgba(82, 113, 165, 0.1)';
                              e.currentTarget.style.color = colors.slate;
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = colors.bgCard;
                              e.currentTarget.style.color = colors.textMuted;
                            }}
                            onClick={(e) => toggleActionMenu(emp.id, e.currentTarget)}
                            title="Actions"
                          >
                            ⋮
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    );
  };

  const renderRecruitingView = () => (
    <div style={{ marginTop:'10px', fontSize:'0.9rem' }}>
      <h3>Recruiting</h3>
      <p>... your Recruiting data ...</p>
    </div>
  );

  const renderDocumentsView = () => (
    <div style={{ marginTop:'10px', fontSize:'0.9rem' }}>
      <h3>Documents</h3>
      <p>... all Documents ...</p>
    </div>
  );

  const handleTabChange = (tab: EmployeesTabKey) => {
    if (tab === 'payrolls') {
      navigate('/employees/payrolls');
    } else if (tab === 'documents') {
      navigate('/employees/documents');
    } else if (tab === 'recruiting') {
      navigate('/employees/recruiting');
    } else {
      setActiveTab(tab);
    }
  };

  // ----------------------------------
  // RENDER
  // ----------------------------------
  return (
    <div style={{ padding: '16px', position: 'relative', fontSize: '0.9rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {resendError && (
        <div style={{ color: 'red', marginBottom: '10px' }}>
          {resendError}
        </div>
      )}

      {/* Glass Header */}
      <div
        className="glass-header"
        style={{ padding: '12px 16px', marginBottom: '16px', position: 'relative', zIndex: 100 }}
      >
        {/* Row 1: Tabs (left) + Add Button (right) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          {/* Tab Navigation */}
          <div data-testid="emp-tabs" style={{ display: 'flex', gap: '4px' }}>
            {visibleTabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  data-testid={`tab-${tab.key}`}
                  onClick={() => !tab.disabled && handleTabChange(tab.key)}
                  disabled={tab.disabled}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: isActive ? '1px solid rgba(82, 113, 165, 0.2)' : '1px solid transparent',
                    background: isActive ? 'rgba(82, 113, 165, 0.12)' : 'transparent',
                    color: isActive ? colors.slate : colors.textMuted,
                    borderRadius: '9999px',
                    cursor: tab.disabled ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    transition: 'all 0.15s',
                    opacity: tab.disabled ? 0.5 : 1,
                    position: 'relative',
                  }}
                  onMouseEnter={e => {
                    if (!isActive && !tab.disabled) {
                      e.currentTarget.style.background = 'rgba(82, 113, 165, 0.06)';
                      e.currentTarget.style.color = colors.slate;
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive && !tab.disabled) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = colors.textMuted;
                    }
                  }}
                >
                  {tab.label}
                  {tab.disabled && (
                    <span style={{ position: 'absolute', top: '-4px', right: '-4px' }}>
                      <ComingSoonBadge />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

        </div>

        {/* Row 2: WA Filter Dropdown (center) + Import/Add Buttons (right) */}
        {activeTab === 'employees' && (() => {
          const activeFilterCount = hiddenWorkAreas.length;
          const totalWAs = Object.keys(workAreaMap).length;
          const visibleCount = totalWAs - activeFilterCount;

          return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            {/* WA Filter Dropdown - Centered */}
            <div ref={waFilterRef} style={{ position: 'relative', zIndex: 1000 }}>
              <button
                onClick={() => setShowWADropdown(!showWADropdown)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '9999px',
                  border: activeFilterCount > 0 ? '1px solid rgba(82, 113, 165, 0.3)' : `1px solid ${colors.border}`,
                  fontSize: '11px',
                  fontWeight: 600,
                  background: activeFilterCount > 0 ? 'rgba(82, 113, 165, 0.4)' : 'rgba(255, 255, 255, 0.9)',
                  color: activeFilterCount > 0 ? colors.slate : colors.textBody,
                  cursor: 'pointer',
                  outline: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {activeFilterCount > 0 ? `Work Areas (${visibleCount}/${totalWAs})` : 'All Work Areas'}
                <span style={{ fontSize: '8px' }}>▼</span>
              </button>

              {showWADropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginTop: '4px',
                    background: colors.bgCard,
                    borderRadius: '16px',
                    padding: '16px',
                    minWidth: '280px',
                    boxShadow: shadows.elevated,
                    border: `1px solid ${colors.border}`,
                    zIndex: 1000,
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {Object.entries(workAreaMap).map(([id, waEntry]: [string, any]) => {
                      const waId = parseInt(id, 10);
                      const name = waEntry?.name || `WA#${waId}`;
                      const isVisible = !hiddenWorkAreas.includes(waId);
                      const waColor = waEntry?.color || '#5271A5';

                      return (
                        <div
                          key={id}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            if (isVisible) {
                              setHiddenWorkAreas(prev => [...prev, waId]);
                            } else {
                              setHiddenWorkAreas(prev => prev.filter(x => x !== waId));
                            }
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              if (isVisible) {
                                setHiddenWorkAreas(prev => [...prev, waId]);
                              } else {
                                setHiddenWorkAreas(prev => prev.filter(x => x !== waId));
                              }
                            }
                          }}
                          style={{
                            padding: '7px 16px',
                            borderRadius: '9999px',
                            border: isVisible ? `2px solid ${waColor}` : '2px solid #e2e8f0',
                            background: isVisible ? waColor : 'transparent',
                            color: isVisible ? 'white' : '#94a3b8',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {name}
                        </div>
                      );
                    })}
                  </div>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={() => setHiddenWorkAreas([])}
                      style={{
                        marginTop: '12px',
                        padding: '4px 8px',
                        borderRadius: '9999px',
                        border: 'none',
                        background: 'transparent',
                        color: colors.textMuted,
                        fontSize: '10px',
                        cursor: 'pointer',
                        width: '100%',
                        textAlign: 'center',
                      }}
                    >
                      Show All
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Search & Status Filter - Left */}
            <div style={{ position: 'absolute', left: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Search Input */}
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                data-testid="employee-search-input"
                style={{
                  padding: '6px 12px',
                  borderRadius: '10px',
                  border: `1px solid ${colors.border}`,
                  fontSize: '11px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: colors.textBody,
                  width: '140px',
                  outline: 'none',
                }}
              />
              {/* Status Filter Button + Dropdown */}
              <div ref={statusFilterRef} style={{ position: 'relative', zIndex: 1000 }}>
                <button
                  onClick={() => setShowStatusModal(!showStatusModal)}
                  data-testid="employee-status-filter"
                  style={{
                    padding: '6px 12px',
                    borderRadius: '9999px',
                    border: onboardingStatusFilter.length > 0 ? '1px solid rgba(82, 113, 165, 0.3)' : `1px solid ${colors.border}`,
                    fontSize: '11px',
                    fontWeight: 600,
                    background: onboardingStatusFilter.length > 0 ? 'rgba(82, 113, 165, 0.4)' : 'rgba(255, 255, 255, 0.9)',
                    color: onboardingStatusFilter.length > 0 ? colors.slate : colors.textBody,
                    cursor: 'pointer',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {onboardingStatusFilter.length > 0 ? `${onboardingStatusFilter.length} Status` : 'All Statuses'}
                  <span style={{ fontSize: '8px' }}>▼</span>
                </button>

                {/* Status Dropdown */}
                {showStatusModal && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '4px',
                      background: colors.bgCard,
                      borderRadius: '16px',
                      padding: '12px',
                      minWidth: '200px',
                      boxShadow: shadows.elevated,
                      border: `1px solid ${colors.border}`,
                      zIndex: 1000,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {EmployeeService.getOnboardingStatusOptions().filter(opt => opt.value !== '').map(option => {
                        const isChecked = onboardingStatusFilter.includes(option.value);
                        return (
                          <label
                            key={option.value}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              background: isChecked ? 'rgba(82, 113, 165, 0.08)' : 'transparent',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => {
                              if (!isChecked) e.currentTarget.style.background = 'rgba(0, 0, 0, 0.03)';
                            }}
                            onMouseLeave={e => {
                              if (!isChecked) e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                if (isChecked) {
                                  setOnboardingStatusFilter(prev => prev.filter(s => s !== option.value));
                                } else {
                                  setOnboardingStatusFilter(prev => [...prev, option.value]);
                                }
                              }}
                              style={{ width: '14px', height: '14px', accentColor: colors.slate, cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '11px', color: isChecked ? colors.slate : colors.textBody, fontWeight: isChecked ? 600 : 400 }}>
                              {option.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {onboardingStatusFilter.length > 0 && (
                      <button
                        onClick={() => setOnboardingStatusFilter([])}
                        style={{
                          marginTop: '8px',
                          padding: '4px 8px',
                          borderRadius: '9999px',
                          border: 'none',
                          background: 'transparent',
                          color: colors.textMuted,
                          fontSize: '10px',
                          cursor: 'pointer',
                          width: '100%',
                          textAlign: 'center',
                        }}
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Import + Add Employee + Bulk Actions Buttons - Right */}
            <div style={{ position: 'absolute', right: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isAdmin && (
                <div ref={bulkMenuRef} style={{ position: 'relative' }}>
                  <button
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 14px',
                      fontSize: '11px',
                      fontWeight: 600,
                      borderRadius: 9999,
                      border: 'none',
                      cursor: selectedEmpIds.size > 0 ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      lineHeight: '20px',
                      background: selectedEmpIds.size > 0
                        ? `linear-gradient(90deg, ${colors.navy} 0%, ${colors.navy} 30%, ${colors.magenta} 100%)`
                        : colors.bgMuted,
                      color: selectedEmpIds.size > 0 ? '#fff' : colors.textLight,
                      boxShadow: selectedEmpIds.size > 0 ? shadows.card : shadows.soft,
                      opacity: bulkBusy ? 0.6 : 1,
                    }}
                    disabled={selectedEmpIds.size === 0 || bulkBusy}
                    onClick={() => setBulkMenuOpen(prev => !prev)}
                    onMouseEnter={e => {
                      if (selectedEmpIds.size > 0) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = shadows.medium;
                      }
                    }}
                    onMouseLeave={e => {
                      if (selectedEmpIds.size > 0) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = shadows.card;
                      }
                    }}
                    title={selectedEmpIds.size === 0 ? 'Select employees first' : `Actions (${selectedEmpIds.size})`}
                    aria-haspopup="menu"
                    aria-expanded={bulkMenuOpen}
                  >
                    {bulkBusy ? 'Working...' : `Actions (${selectedEmpIds.size})`}
                  </button>

                  {bulkMenuOpen && selectedEmpIds.size > 0 && (
                    <div
                      role="menu"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: 4,
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: 16,
                        padding: 8,
                        minWidth: 180,
                        boxShadow: shadows.elevated,
                        zIndex: 1000,
                      }}
                    >
                      <div style={{ fontSize: 10, color: colors.textMuted, fontWeight: 600, padding: '4px 10px', marginBottom: 2 }}>
                        Change Status
                      </div>
                      {(['activate', 'deactivate', 'reactivate'] as const).map(action => (
                        <div
                          key={action}
                          role="menuitem"
                          style={{
                            ...actionItemStyle,
                            color: action === 'deactivate' ? colors.danger : colors.textBody,
                          }}
                          onClick={() => handleBulkStatusChange(action)}
                          onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          {action.charAt(0).toUpperCase() + action.slice(1)}
                        </div>
                      ))}
                      <div style={{ height: 1, background: colors.borderLight, margin: '4px 0' }} />
                      <div
                        role="menuitem"
                        style={actionItemStyle}
                        onClick={() => { setBulkPasswordOpen(true); setBulkMenuOpen(false); }}
                        onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        Change Password
                      </div>
                      <div style={{ height: 1, background: colors.borderLight, margin: '4px 0' }} />
                      <div
                        role="menuitem"
                        style={actionItemStyle}
                        onClick={() => {
                          setSelectedEmpIds(new Set(activeEmployees.map(e => e.id)));
                          setBulkMenuOpen(false);
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        Select All ({activeEmployees.length})
                      </div>
                      <div
                        role="menuitem"
                        style={{ ...actionItemStyle, color: colors.textMuted }}
                        onClick={() => { setSelectedEmpIds(new Set()); setBulkMenuOpen(false); }}
                        onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        Clear Selection
                      </div>
                    </div>
                  )}
                </div>
              )}
              {isAdmin && (
                <button
                  onClick={() => setShowImportModal(true)}
                  style={{
                    width: 36,
                    height: 36,
                    padding: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff',
                    border: `2px solid ${colors.border}`,
                    borderRadius: '50%',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = colors.slate; e.currentTarget.style.color = colors.navy; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.color = colors.textMuted; e.currentTarget.style.transform = 'translateY(0)'; }}
                  title="Import Employees (CSV/XLSX)"
                  aria-label="Import employees from CSV"
                >
                  <FaUpload size={14} />
                </button>
              )}
              <button
                data-testid="add-employee-button"
                className="hover-btn-primary"
                onClick={handleOpenAddModal}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: `linear-gradient(90deg, ${colors.navy} 0%, ${colors.navy} 30%, ${colors.magenta} 100%)`,
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '10px 20px',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(231, 0, 125, 0.15)',
                  transition: 'all 0.2s ease',
                }}
                aria-label="Add employee"
              >
                <FaPlus style={{ fontSize: '9px' }} /> Employee
              </button>
            </div>
          </div>
          );
        })()}
      </div>

      {activeTab === 'employees' && (() => {
        const activeFilters: ActiveFilter[] = [];
        if (searchTerm) {
          activeFilters.push({ key: 'search', label: `Search: ${searchTerm}`, onClear: () => setSearchTerm('') });
        }
        if (hiddenWorkAreas.length > 0) {
          const totalWAs = Object.keys(workAreaMap).length;
          const visibleCount = totalWAs - hiddenWorkAreas.length;
          activeFilters.push({ key: 'wa', label: `Work Areas (${visibleCount}/${totalWAs})`, onClear: () => setHiddenWorkAreas([]) });
        }
        if (onboardingStatusFilter.length > 0) {
          activeFilters.push({ key: 'status', label: `${onboardingStatusFilter.length} Status`, onClear: () => setOnboardingStatusFilter([]) });
        }
        return (
          <ActiveFiltersBar
            filters={activeFilters}
            onClearAll={() => {
              setSearchTerm('');
              setHiddenWorkAreas([]);
              setOnboardingStatusFilter([]);
            }}
          />
        );
      })()}

      {activeTab === 'employees' && (
        <EmployeeInfoBanner activeEmployees={activeEmployees} />
      )}

      {activeTab === 'employees' && renderEmployeesTable()}
      {activeTab === 'payrolls' && (
        <>
          {renderEmployeesTable()}
        </>
      )}
      {activeTab === 'recruiting' && renderRecruitingView()}
      {activeTab === 'documents'  && renderDocumentsView()}

      <AddEmployeeModal
        open={showAddModal}
        onClose={handleCloseAddModal}
        onInvite={handleInviteNewEmployee}
      />

      <FinalizeEmployeeModal
        open={showFinalizeModal}
        employee={selectedEmployee}
        onClose={handleCloseFinalizeModal}
        onConfirm={handleFinalizeEmployee}
        computeWorkAreas={computeWorkAreas}
        computeWeeklyHours={computeWeeklyHours}
      />

      <ChangePasswordModal
        open={showPasswordModal && !!selectedEmployeeForPassword}
        onClose={handleClosePasswordModal}
        mode="single"
        employeeName={selectedEmployeeForPassword ? `${selectedEmployeeForPassword.first_name} ${selectedEmployeeForPassword.last_name}` : ''}
        onSubmit={handleChangePassword}
      />

      <ChangePasswordModal
        open={bulkPasswordOpen}
        onClose={() => setBulkPasswordOpen(false)}
        mode="bulk"
        selectedCount={selectedEmpIds.size}
        onSubmit={handleBulkPasswordChange}
        busy={bulkBusy}
      />

      {openActionMenuFor && actionMenuPos && createPortal(
        <EmployeeActionMenu
          employee={sortedEmployees.find(e => e.id === openActionMenuFor)}
          actionMenuPos={actionMenuPos}
          onClose={() => { setOpenActionMenuFor(null); setActionMenuPos(null); }}
          onEdit={(emp) => navigate(`/employees/${emp.id}`)}
          onChangePassword={handleOpenPasswordModal}
          onResendInvite={handleResendInvite}
          onActivate={handleActivateUser}
          onReactivate={handleReactivateUser}
          onDeactivate={handleDeactivateUser}
          onFinalize={handleOpenFinalizeModal}
          activatingUser={activatingUser}
          resendInviteEmployeeId={resendInviteEmployeeId}
        />,
        document.body
      )}

      {/* Import Employees Modal */}
      <ImportEmployeesModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onCommitted={(importedEmails) => {
          reloadEmployeeList();
          if (importedEmails?.length) {
            setHighlightedEmails(new Set(importedEmails));
            setTimeout(() => setHighlightedEmails(new Set()), 4000);
          }
        }}
      />

    </div>
  );
}

/** ---------- STYLES ---------- */
const actionItemStyle: React.CSSProperties = {
  padding:'8px 12px',
  cursor:'pointer',
  borderRadius: '10px',
  fontSize: '12px',
  color: colors.textBody,
  transition: 'background 0.15s',
};
const badgeStyle = (badgeType) => {
  const color = (badgeType === 'greenBadge') ? 'green' : '#999';
  return {
    display:'inline-block',
    backgroundColor:color,
    borderRadius:'4px',
    padding:'2px 5px',
    color:'#fff',
    fontSize:'0.7rem'
  };
};

export default Employees;
